/**
 * Recursion bound for reference chips.
 *
 * A chip's label is itself model-authored text (`product.name`,
 * `version.contentTitle`, `collab.shortestSummary`), so it renders through
 * markdown like every other model-authored surface. That closes a loop: a
 * product named "rollup of [[p3]]" would render a chip whose label renders a
 * chip. Two products naming each other would never terminate.
 *
 * The prompt's ref rule tells models not to reference a node inside its own
 * name, but the renderer must not depend on the model obeying it — so the
 * depth is bounded structurally instead.
 *
 * Depth 0 is an ordinary chip. Everything a chip renders sits at depth 1,
 * where `Creference` degrades to the resolved label as plain text: still the
 * entity's real name rather than a raw `[[p3]]` token, but with no markdown
 * pass beneath it, so the recursion terminates in exactly one step.
 */

"use client";

import { createContext, useContext, type ReactNode } from "react";

/** The first depth at which a reference stops rendering as an interactive chip. */
export const CREFERENCE_NESTED_DEPTH = 1;

const CreferenceDepthContext = createContext<number>(0);

export function useCreferenceDepth(): number {
  return useContext(CreferenceDepthContext);
}

export function useIsNestedCreference(): boolean {
  return useCreferenceDepth() >= CREFERENCE_NESTED_DEPTH;
}

/**
 * Wraps a chip's own rendering so every reference inside its label resolves one
 * level deeper than the chip itself.
 */
export function CreferenceDepthBoundary({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const depth = useCreferenceDepth();

  return (
    <CreferenceDepthContext.Provider value={depth + 1}>
      {children}
    </CreferenceDepthContext.Provider>
  );
}
